import { Blueprint } from '../../blueprint.class';
import { BlueprintInstanceType } from '../../../shared.enums';
import { PanelItem } from '../../../panel-items/panel-item.class';
import { Vertex3 } from '../../../../spatial-classes';
import { Size } from '../../../../types';
import { ToolResult } from '../../types';
import {
	COMPONENT_PLACEMENT_FRONT_ONLY,
	COMPONENT_PLACEMENT_TOP_ONLY,
	COMPONENT_PLACEMENT_FRONT_AND_TOP,
	COMPONENT_PLACEMENT_BOTTOM_ONLY
} from '../../../../component-classes/constants';
import { cleanKitchenTool } from './clean-kitchen.tool';

/**
 * Vertically align the appliance group the new appliance landed in. Runs as
 * the LAST step of every add, after clean_kitchen and on its failure path too.
 *
 * clean_kitchen already aligns every group on the island, but not always last:
 * it can throw (the catch branches below return without having aligned), and
 * the group moves it makes re-fire the rules engine, which can add or remove
 * generated items in a group after that group was aligned. Aligning the new
 * appliance's own group at the very end guarantees it settles aligned.
 * centerItemsVertically recomputes positions from scratch, so repeating it is
 * harmless. The group is read from the item's live refMap, not the local one,
 * in case the layout pass moved the item.
 */
const alignNewApplianceGroup = (item: PanelItem): void => {
	item.refMap?.panelItemGroup?.centerItemsVertically();
};

export interface AddApplianceArgs {
	sku: string;
	islandId?: string;
	applianceId?: string;
	placement?: 'left' | 'right' | 'above' | 'below';
	autoSize?: boolean;
}

export const addApplianceTool = (blueprint: Blueprint, { sku, islandId, applianceId, placement, autoSize }: AddApplianceArgs): ToolResult<{ itemId: string }> => {
	if (!sku) return { success: false, error: 'Missing sku. Use get_appliances with a valid category to get available SKUs.' };

	const kitchen = blueprint.kitchens.getActiveVersion();
	if (!kitchen) return { success: false, error: 'No active kitchen found.' };

	// Auto-resolve islandId for single-island designs
	let resolvedIslandId = islandId;
	if (!resolvedIslandId) {
		const islands = kitchen.design.islands.children;
		if (islands.length === 0) {
			return { success: false, errorCode: 'NOT_FOUND', error: 'No islands in the design.' };
		}
		if (islands.length === 1) {
			resolvedIslandId = islands[0].id;
		} else {
			return {
				success: false,
				errorCode: 'INVALID_PARAM',
				error: `${islands.length} islands exist. Specify islandId. Use list_islands to get valid IDs.`,
				recoveryHint: 'Call list_islands first, then provide the islandId parameter.'
			};
		}
	}

	const island = kitchen.design.islands.children.find((i) => i.id === resolvedIslandId);
	if (!island) return { success: false, errorCode: 'NOT_FOUND', error: `Island '${resolvedIslandId}' not found. Use list_islands to get valid island IDs.` };

	// Verify SKU exists
	const component = blueprint.context.components.findSku(sku);
	if (!component) return { success: false, error: `SKU '${sku}' not found. Use get_appliances with a valid category to get available SKUs.` };

	// Adding to front panel as default action
	const frontPanel = island.panels.children.find((p) => p.id === island.panels.frontPanelId);
	if (!frontPanel) return { success: false, error: `Front panel for island '${resolvedIslandId}' not found.` };

	// Validate placement value if provided
	if (placement && !['left', 'right', 'above', 'below'].includes(placement)) {
		return { success: false, error: "Invalid placement. Must be 'left', 'right', 'above', or 'below'." };
	}

	// Resolve reference appliance when applianceId is provided
	let refPanelItem: PanelItem | null = null;
	let refGroup = null;

	if (applianceId) {
		const [refType, refItem] = kitchen.getReferenceItem(applianceId);
		if (!refItem) return { success: false, error: `Reference appliance '${applianceId}' not found. Use list_appliances to get valid item IDs.` };

		if (refType !== BlueprintInstanceType.PanelItem) {
			return { success: false, error: `Reference item '${applianceId}' is not an appliance (type: ${refType}).` };
		}

		refPanelItem = refItem as PanelItem;
		refGroup = refPanelItem.refMap.panelItemGroup;

		if (!refGroup) return { success: false, error: `Could not find appliance group for reference '${applianceId}'.` };
	}

	// Get the new appliance width
	const newApplianceWidth = component.size.width;

	// ── SLEEVE: add sleeve-compatible appliance to a sleeve group ──
	if (applianceId && refGroup) {
		const groupHasSleeve = refGroup.items.children.some((pi) => pi.item.component.sleeveDepth !== null);

		if (groupHasSleeve) {
			const nonSleeveItems = refGroup.items.children.filter((pi) => pi.item.component.sleeveDepth === null);
			if (nonSleeveItems.length > 0) {
				return { success: false, error: 'This sleeve already has an appliance. Remove the existing appliance before adding a new one.' };
			}

			const item = refGroup.items.newFromSku(sku);
			item.center.y = refPanelItem.center.y + (placement === 'above' ? 1 : -1);

			try {
				cleanKitchenTool(blueprint, { islandId: resolvedIslandId, autoResize: autoSize });
			} catch {
				alignNewApplianceGroup(item);
				return {
					success: true,
					data: { itemId: item.id },
					message: 'Appliance added but auto-layout failed. Run clean_kitchen to fix spacing.'
				};
			}
			alignNewApplianceGroup(item);

			return {
				success: true,
				message: `Sleeve-compatible appliance '${sku}' added to sleeve group at '${applianceId}' on island '${resolvedIslandId}'.`,
				data: { itemId: item.id }
			};
		}
	}

	// ── ABOVE / BELOW: add to reference appliance's existing group ──
	// Bottom-only appliances always join the reference group regardless of placement direction
	const isBottomOnly = component.placement === COMPONENT_PLACEMENT_BOTTOM_ONLY;
	if (applianceId && (placement === 'above' || placement === 'below' || isBottomOnly)) {
		const newPlacement = component.placement;
		const existingItems = refGroup.items.children;

		// Validate: top / front-and-top conflict
		if (
			(newPlacement === COMPONENT_PLACEMENT_FRONT_AND_TOP || newPlacement === COMPONENT_PLACEMENT_TOP_ONLY) &&
			existingItems.some((pi) =>
				pi.item.placement === COMPONENT_PLACEMENT_TOP_ONLY ||
				pi.item.placement === COMPONENT_PLACEMENT_FRONT_AND_TOP
			)
		) {
			return {
				success: false,
				error: 'Cannot add a top or front-and-top appliance to a group that already has a top-mounted appliance.'
			};
		}

		// Validate: bottom-only conflict
		if (
			newPlacement === COMPONENT_PLACEMENT_BOTTOM_ONLY &&
			existingItems.some((pi) =>
				pi.item.placement === COMPONENT_PLACEMENT_FRONT_ONLY ||
				pi.item.placement === COMPONENT_PLACEMENT_BOTTOM_ONLY ||
				pi.item.placement === COMPONENT_PLACEMENT_FRONT_AND_TOP
			)
		) {
			return {
				success: false,
				error: 'Cannot add a bottom-only appliance to a group that already has front, bottom, or front-and-top appliances.'
			};
		}

		// Validate: front-only height check
		if (newPlacement === COMPONENT_PLACEMENT_FRONT_ONLY) {
			const panel = refPanelItem.refMap.panel;
			const topBoundItem = refGroup.getTopBoundItem();
			const bottomBoundItem = refGroup.getBottomBoundItem();
			const thickness = island.getCountertop()?.material.thickness ?? 0;
			let distanceBelowCountertop = topBoundItem?.item.distanceBelowCountertop ?? 0;
			if (distanceBelowCountertop > 0) distanceBelowCountertop -= thickness;

			const netPanelHeight = panel.size.height -
				distanceBelowCountertop -
				(bottomBoundItem?.item.size.height ?? 0);

			const frontItems = refGroup.items.filterItems(false, [COMPONENT_PLACEMENT_FRONT_ONLY]);
			const totalFrontHeight = frontItems.reduce((acc, pi) => acc + pi.item.size.height, 0);

			if (totalFrontHeight + component.size.height >= netPanelHeight) {
				return {
					success: false,
					error: `Not enough vertical space in the group to add this appliance. ` +
						`Available: ${netPanelHeight - totalFrontHeight}", needed: ${component.size.height}".`
				};
			}
		}

		// Add to existing group
		const item = refGroup.items.newFromSku(sku);

		// Set y position relative to reference for ordering.
		// centerItemsVertically sorts ascending by y then distributes bottom-to-top,
		// so a smaller y ends up at the bottom. 'below' needs a smaller y to sort first.
		const yOffset = placement === 'above' ? 1 : -1;
		item.center.y = refPanelItem.center.y + yOffset;

		// An added appliance re-spaces the run: hand-placed positions on the panel are forgotten
		refGroup.refMap?.panel?.resetFineTune?.();

		// Clean the island to distribute appliances
		try {
			cleanKitchenTool(blueprint, { islandId: resolvedIslandId, autoResize: autoSize });
		} catch {
			alignNewApplianceGroup(item);
			return {
				success: true,
				data: { itemId: item.id },
				message: 'Appliance added but auto-layout failed. Run clean_kitchen to fix spacing.'
			};
		}
		alignNewApplianceGroup(item);

		return {
			success: true,
			message: `Appliance '${sku}' added ${placement} '${applianceId}' on island '${resolvedIslandId}'.`,
			data: { itemId: item.id }
		};
	}

	// ── LEFT / RIGHT / DEFAULT: create a new appliance group ──

	// Determine x position for the new group
	const halfWidth = frontPanel.size.width / 2;
	let newX: number;

	if (applianceId && refGroup && (placement === 'left' || placement === 'right')) {
		// Relative to reference appliance's group
		const offset = placement === 'left' ? -1 : 1;
		newX = refGroup.center.x + offset;
	} else if (placement === 'left') {
		// Left edge of island
		newX = -halfWidth + 1;
	} else {
		// Default (no placement) or right: right edge of island
		newX = halfWidth - 1;
	}

	// Width validation — always creating a new group in this branch.
	//
	// SCOPED TO THE PANEL WE ARE INSERTING INTO. This used to sum every panel
	// on the island, but the new group only ever lands on the front panel, so
	// side- and back-panel contents were charged against the front panel's
	// horizontal budget. Those panels hold ventilation and utility passthrough
	// — on a real 127" grill island that was 50" of phantom occupancy (a
	// 42" vent set plus two 4" passthroughs), leaving 5" free where 60" was
	// actually available and rejecting a 24" burner that fits comfortably.
	// A 34" pizza island reported 76" used, which is impossible for anything
	// laid out horizontally.
	//
	// cleanKitchenTool already scopes to `panels.getFrontPanel()` when it
	// sizes and distributes; this brings the fit check in line with the
	// layout that actually happens.
	//
	// The budget now comes from `frontPanel.availableWidth` rather than being
	// restated here. Two things change as a result:
	//
	//  - Connected-island overlap is subtracted. `distributeApplianceGroups-
	//    Horizontally` has always clamped placement to the overlap-adjusted
	//    edges while this check budgeted the panel's full nominal width, so on
	//    an L-shape the gate admitted appliances the layout had no room for.
	//  - The old arithmetic charged spacing twice: `additionalWidthNeeded` added
	//    1" when any group existed AND `spacingReduction` added one per group,
	//    i.e. g+1 inches of air for g gaps. One inch is now charged per gap.
	const availableWidth = frontPanel.availableWidth;
	const currentIslandWidth = (island.size as Size).width;

	const canAutoSize = autoSize && !island.sizeLocked;

	if (!canAutoSize && newApplianceWidth > availableWidth) {
		if (!autoSize) {
			const requiredAdditionalWidth = Math.ceil(newApplianceWidth - availableWidth);
			const requiredWidth = currentIslandWidth + requiredAdditionalWidth;
			return {
				success: false,
				error: `Island '${resolvedIslandId}' is not wide enough to add this appliance. ` +
					`The island needs to be ${requiredAdditionalWidth}" wider (current: ${currentIslandWidth}", required: ${requiredWidth}"). ` +
					`Use resize_island to increase the width before adding this appliance.`
			};
		}
		// autoSize was requested but island is locked — proceed without resizing
	}

	// Create new group and position it
	const group = frontPanel.panelItemGroups.new();
	group.center = new Vertex3({ dataIn: { x: newX } });

	// Add the item
	const item = group.items.newFromSku(sku);

	// An added appliance re-spaces the run: hand-placed positions on the panel are forgotten
	group.refMap?.panel?.resetFineTune?.();

	// Clean the island to distribute appliances
	try {
		cleanKitchenTool(blueprint, { islandId: resolvedIslandId, autoResize: autoSize });
	} catch {
		alignNewApplianceGroup(item);
		return {
			success: true,
			data: { itemId: item.id },
			message: 'Appliance added but auto-layout failed. Run clean_kitchen to fix spacing.'
		};
	}
	alignNewApplianceGroup(item);

	const positionDesc = applianceId
		? `${placement} of appliance '${applianceId}'`
		: placement
			? `${placement} side of the island`
			: 'right side of the island';

	return {
		success: true,
		message: `Appliance '${sku}' added to the ${positionDesc} on island '${resolvedIslandId}'.`,
		data: { itemId: item.id }
	};
};
